import React, { useEffect, useState, useCallback } from 'react'
import { InView } from 'react-intersection-observer'
import { LinkButton } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { HeadingSM, ParagraphSM, TitleLG } from '@/atoms/Text'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useShopifyUserAttributes } from '@/views/PreSales/hooks/useShopifyUserAttributes'
import { RodeoTicketOption } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/RodeoTicketOption'
import { calculateDiscountPercentage, getRodeoDiscountCode } from './experiment/discountCodeUtils'

const OPTIONS = [4, 6, 10]

interface DiscountRate {
  quantity: number
  discountRate: number
}

interface Props {
  ticketPrice: number
  theatricalSlug: string
  groupTicketProductVariantId: string
  discountRates: DiscountRate[]
  expImageSrc: string
}

export const ShopifyGroupTicketsLastRodeo: React.FC<Props> = ({
  ticketPrice,
  groupTicketProductVariantId,
  theatricalSlug,
  discountRates,
  expImageSrc,
}) => {
  const { t } = useTranslate('tickets')
  const track = useSafeTrack()
  const { query } = useSafeRouter()
  const { trackProductAdded, trackProductViewed } = useEcommerceEvents()
  const [selectedQuantity, setSelectedQuantity] = useState(OPTIONS[0])
  const [isProcessing, setIsProcessing] = useState(false)
  const { createCart, applyDiscountCode } = useShopifyCart()
  const [shopifyCheckoutUrl, setShopifyCheckoutUrl] = useState('')

  const attributes = useShopifyUserAttributes('theatrical-group-tickets', 'theatrical group tickets', theatricalSlug)

  // Find the discount rate for selected quantity
  const getDiscountRate = useCallback(
    (quantity: number): number => {
      if (quantity === 4) {
        return 0.75
      }
      if (quantity === 6) {
        return 0.6666666
      }

      if (!discountRates || !Array.isArray(discountRates) || discountRates.length === 0) {
        return 0.5
      }

      const found = discountRates.find((rate) => rate.quantity === quantity)
      return found ? found.discountRate : 0.5 // Fallback to 50% if not found
    },
    [discountRates],
  )
  const getDiscountCodeForQuantity = useCallback((quantity: number): string => {
    return getRodeoDiscountCode(quantity)
  }, [])

  useEffect(() => {
    if (!selectedQuantity) return

    setIsProcessing(true)
    const createShopifyCheckoutUrl = async () => {
      try {
        const { cart } = await createCart({
          lines: [
            {
              quantity: selectedQuantity,
              merchandiseId: `gid://shopify/ProductVariant/${groupTicketProductVariantId}`,
            },
          ],
          attributes,
        })

        if (!cart?.id) return null

        const discountCodeParam = getItemOrFirstEntry(query?.groupDiscountCode)
        const discountCode = discountCodeParam ? discountCodeParam : getDiscountCodeForQuantity(selectedQuantity)

        const finalCart = await applyDiscountCode({ cartId: cart.id, discountCodes: [discountCode] })

        if (!finalCart?.cart?.checkoutUrl) return

        setShopifyCheckoutUrl(finalCart?.cart?.checkoutUrl)
      } catch (err) {
        logger().error('Failed to create shopifyCheckoutUrl for group tickets', err)
      } finally {
        setIsProcessing(false)
      }
    }

    createShopifyCheckoutUrl()
  }, [
    applyDiscountCode,
    attributes,
    createCart,
    groupTicketProductVariantId,
    query?.groupDiscountCode,
    selectedQuantity,
    getDiscountCodeForQuantity,
  ])

  useEffect(() => {
    track('Group Tickets Options Viewed')
  }, [track])

  const handleCtaClick = async (quantity: number) => {
    try {
      const discountRate = getDiscountRate(quantity)
      const discountPercent = calculateDiscountPercentage(discountRate)
      const discountCode = getDiscountCodeForQuantity(quantity)
      // Use a known valid analytics event
      track('Group Tickets Checkout Started', {
        quantity,
        discountPercentage: discountPercent,
        discountCode,
      })

      trackProductAdded({
        category: 'group tickets',
        funnel: 'theatrical',
        name: `Group Ticket ${theatricalSlug}`,
        price: Number((ticketPrice * discountRate).toFixed(2)),
        currency: 'USD',
        quantity: quantity,
        product_id: `group-tickets-${theatricalSlug}`,
        sku: `group-tickets-${theatricalSlug}`,
        projectSlug: theatricalSlug,
      })
    } catch (err) {
      logger().error('Failed to create group ticket checkout', err)
    }
  }

  return (
    <InView
      triggerOnce
      className={'max-h-[600px] overflow-hidden'}
      onChange={(inView) => {
        if (!inView) return

        const discountRate = getDiscountRate(selectedQuantity)

        trackProductViewed({
          category: 'group tickets',
          funnel: 'theatrical',
          name: `Group Ticket ${theatricalSlug}`,
          price: Number((ticketPrice * discountRate).toFixed(2)),
          currency: 'USD',
          quantity: selectedQuantity,
          product_id: `group-tickets-${theatricalSlug}`,
          sku: `group-tickets-${theatricalSlug}`,
          url: '',
          projectSlug: theatricalSlug,
          value: selectedQuantity * ticketPrice * discountRate,
        })
      }}
    >
      <>
        <div className={'text-center'}>
          <HeadingSM weight={'bold'} color={'core-gray-950'} className={'hidden md:block'}>
            {t('groupTheBiggerTheCrew', 'The Bigger the Crew,')}
          </HeadingSM>
          <TitleLG className={'md:hidden'} weight={'bold'}>
            {t('groupTheBiggerTheCrew', 'The Bigger the Crew,')}
          </TitleLG>
          <HeadingSM weight={'bold'} color={'core-gray-950'} className={'hidden md:block'}>
            {t('groupTheBiggerTheSavings', 'The Bigger the Savings!')}
          </HeadingSM>

          <TitleLG className={'md:hidden'} weight={'bold'}>
            {t('groupTheBiggerTheSavings', 'The Bigger the Savings!')}
          </TitleLG>
        </div>
        <ParagraphSM color={'core-gray-700'} className={'mt-2 text-center text-core-gray-700 md:mt-3'}>
          <Translate t={t} i18nKey="groupRodeoTakeYourFamily">
            Take your family to the movies and earn free tickets. Use them to bring friends or family. Only at Angel
            Partner Theaters.
          </Translate>
        </ParagraphSM>
      </>

      <div className="my-6 flex justify-center md:my-8">
        <form className="flex w-full flex-col gap-2" onSubmit={(e) => e.preventDefault()}>
          {groupTicketProductVariantId &&
            OPTIONS.map((quantity) => {
              const title = t('groupTicketTitle', '{{quantity}} Tickets', { quantity })
              const isSelected = selectedQuantity === quantity
              let subTitle = ''
              if (quantity === 4) {
                subTitle = 'Buy 3 → Get 1 Free'
              } else if (quantity === 6) {
                subTitle = 'Buy 4 → Get 2 Free'
              } else if (quantity === 10) {
                subTitle = 'Buy 5 → Get 5 Free'
              }
              return (
                <RodeoTicketOption
                  key={quantity}
                  onClick={() => {
                    setSelectedQuantity(quantity)
                  }}
                  title={title}
                  ticketPrice={ticketPrice}
                  ticketQuantity={quantity}
                  discountRate={getDiscountRate(quantity)}
                  selected={isSelected}
                  subTitle={subTitle}
                />
              )
            })}
        </form>
      </div>

      <FlexColumn className=" min-h-[54px] items-center gap-4 sm:min-h-[130px]">
        {shopifyCheckoutUrl ? (
          <>
            <LinkButton
              external
              href={shopifyCheckoutUrl}
              onClick={() => handleCtaClick(selectedQuantity)}
              className="bg-core-red-700 mx-auto flex w-full max-w-[380px] items-center justify-center gap-1 rounded-[14px] px-7 py-[14px] md:w-fit"
              variant="black"
              disabled={isProcessing}
            >
              {isProcessing && <LoadingSpinnerIcon color="black" size={16} />}
              {t('checkoutGroupTickets', 'Checkout')}
            </LinkButton>
          </>
        ) : (
          <LoadingSpinnerIcon size={32} color="black" />
        )}
      </FlexColumn>
      <div
        className="pointer-events-none absolute bottom-0 right-0 z-0 hidden md:block"
        style={{
          backgroundImage: `url(${expImageSrc})`,
          backgroundPosition: '20px 35px',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '234px auto',
          width: '234px',
          height: '202px',
        }}
      />
    </InView>
  )
}
